import {DOCUMENT, inject, Injectable, OnDestroy, signal, WritableSignal} from '@angular/core';
import {AppService} from '../app/app.service';
import {AuthQuery} from '../../queries/auth.query';
import {AuthStore} from '../../stores/auth.store';
import {DialogService} from '../dialog/dialog.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {NavigationService} from '../navigation/navigation.service';
import {Router} from '@angular/router';
import {firstValueFrom, Observable, skipWhile, Subscription, switchMap} from 'rxjs';
import {MeService as ApiMeService} from '../../api/services/me.service';
import {filter, take} from 'rxjs/operators';
import {AppQuery} from '../../queries/app.query';
import {LanguageService} from '../language/language.service';
import {NotificationService} from '../notification/notification.service';
import {LOGIN_TYPE} from '../../constants/login/login.type.constants';
import {LoginType} from '../../types/login/login.type';
import {AZURE_SERVICE, AzureService} from '../azure/azure.service';
import {TokenService} from '../token/token.service';
import {User} from '../../api/models/user';
import {AzureSignInMethodEnum} from '../../enums/azure-sign-in-method.enum';
import {toObservable} from '@angular/core/rxjs-interop';
import {AkitaMimicService} from '../../modules/akita-mimic/services/akita-mimic.service';
import {AzureLoginFailedError, OrganisationError} from '../../errors/login-errors';
import {ServerSelectionService} from '../server-selection/server-selection.service';
import {AppStorageService} from '../app-storage/app-storage.service';
import {AmagnoStorageKeyEnum} from '../../enums/amagno-storage-key.enum';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {RecentTagValuesService} from '../recent-tag-values/recent-tag-values.service';
import {PanelService} from '../panel/panel.service';
import {RecentSearchService} from '../search/recent-search/recent-search.service';
import {ListService} from '../list/list.service';
import {SentryService} from '../sentry/sentry.service';
import {ReAuthenticationResultEnum} from '../../enums/re-authentication-result.enum';
import {AppSnackbarModal} from '../../models/modal/snackbar/app-snackbar-modal.model';
import {AppSnackbarTypeEnum} from '../../enums/modal/app-snackbar-type.enum';
import {ModalService} from '../modal/modal.service';
import {TranslationKey} from '../../types/available-translations.type';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    private readonly subscriptions: { [func: string]: Subscription | undefined } = {};
    private readonly hasTokenFailed: WritableSignal<boolean> = signal(false);
    private readonly afterLoginNavigationObservable: Observable<boolean>;
    private readonly akitaMimicService = inject(AkitaMimicService);
    private readonly apiMeService = inject(ApiMeService);
    private readonly appQuery = inject(AppQuery);
    private readonly appService = inject(AppService);
    private readonly authQuery = inject(AuthQuery);
    private readonly authStore = inject(AuthStore);
    private readonly azureService = inject<AzureService>(AZURE_SERVICE);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly navigationService = inject(NavigationService);
    private readonly languageService = inject(LanguageService);
    private readonly listService = inject(ListService);
    private readonly notificationService = inject(NotificationService);
    private readonly panelService = inject(PanelService);
    private readonly recentSearchService = inject(RecentSearchService);
    private readonly recentTagValuesService = inject(RecentTagValuesService);
    private readonly router = inject(Router);
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly tokenService = inject(TokenService);
    private readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly appStorageService = inject(AppStorageService);
    private readonly tryToRefreshToken: () => Promise<void>;
    private readonly sentryService = inject(SentryService);
    private readonly modalService = inject(ModalService);
    private authenticationErrorSnackbar: AppSnackbarModal | undefined;
    private pendingReAuthentication: Promise<void> | undefined;

    public constructor() {
        this.afterLoginNavigationObservable = toObservable<boolean>(this.navigationService.getAfterLoginNavigationSignal());
        this.tryToRefreshToken = async (): Promise<void> => {
            if (!this.router.url.includes('login') && !this.router.url.includes('logout')) {
                const reAuthenticationResult = await this.tryToReAuthenticate();

                if (reAuthenticationResult !== ReAuthenticationResultEnum.Success) {
                    await this.logout();
                    await this.navigationService.navigateToLogin();
                    this.showReAuthenticationError(reAuthenticationResult);
                }
            }
        };
    }

    public ngOnDestroy(): void {
        Object.values(this.subscriptions)
            .filter(subscription => subscription !== undefined)
            .forEach(subscription => subscription?.unsubscribe());
    }

    public async tryToReAuthenticate(): Promise<ReAuthenticationResultEnum> {
        if (!this.authQuery.getIsLoggedIn() || (this.authQuery.getIsLoggedIn() && this.appQuery.getLastLoginType() == LOGIN_TYPE.AZURE)) {
            try {
                await new Promise<void>((resolve, reject) => {
                    this.azureService.onSilentSignIn.pipe(take(1))
                        .subscribe((userData: { username?: string, password?: string } | undefined) => {
                            if (userData) {
                                this.updateAuthData(userData.username, userData.password, true);
                                resolve();
                            } else {
                                reject(new AzureLoginFailedError());
                            }
                        });
                    this.azureService.onSignInFailure.pipe(take(1))
                        .subscribe(interActionType => {
                            if (interActionType == AzureSignInMethodEnum.Silent) {
                                reject(new AzureLoginFailedError());
                            }
                        });
                    this.azureService.tryToReLogin()
                        .catch(reject);
                });
            } catch (error) {
                this.appService.setShowSplashScreen(false, 0);
                await this.logout();
                await this.navigationService.navigateToLogin();
                if (!(error instanceof AzureLoginFailedError)) {
                    throw error;
                }

                return ReAuthenticationResultEnum.AzureLoginFailed;
            }
        }

        try {
            const isLoggedInWithSavedCredentials = await this.loginWithSavedCredentials();

            return isLoggedInWithSavedCredentials ? ReAuthenticationResultEnum.Success : ReAuthenticationResultEnum.Failed;
        } catch {
            return ReAuthenticationResultEnum.Failed;
        }
    }

    public async loginWithSavedCredentials(): Promise<boolean> {
        const username = await this.unveilCredentialFromStore(this.authQuery.getUsername());
        const password = await this.unveilCredentialFromStore(this.authQuery.getPassword());
        if (username.length <= 0 || password.length <= 0) {
            return false;
        }

        const isRememberUser = this.authQuery.isRememberUser();
        const loginType: LoginType = this.appQuery.getLastLoginType();
        await this.login(username, password, isRememberUser, loginType);
        this.showSystemMessageIfSet()
            .then();
        return true;
    }

    /**
     * Login the user by authenticating the user with the provided credentials.
     *
     * @param {string} username - The username of the user attempting to authenticate.
     * @param {string} password - The password of the user attempting to authenticate.
     * @param {boolean} rememberUser - The flag if the user wants to remember the credentials to re-login automatically
     * @param {LoginType} loginType - The type of login being used, indicating the authentication method
     * @throws {TokenAuthenticationError} - Error that occurs, when the user could not be authenticated
     * @throws {OrganisationError} - Error that occurs, while logging in
     * @throws {Error} - Error that occurs, while logging in
     */
    public async login(username: string, password: string, rememberUser: boolean = false, loginType: LoginType = LOGIN_TYPE.USERNAME): Promise<void> {
        this.closeAuthenticationErrorSnackbar();
        try {
            await this.tokenService.createToken(loginType, username, password);

            const loggedInUserId = this.tokenService.getUserIdFromTokenPayload();
            const loggedInOrganisationId = this.tokenService.getOrganisationIdFromTokenPayload();
            if (loggedInUserId && loggedInOrganisationId) {
                this.recentTagValuesService.clear();
                this.updateAuthData(username, password, rememberUser);

                this.appService.setLastLoginType(loginType);
                this.tokenService.scheduleTokenRefresh(this.tryToRefreshToken);
                this.hasTokenFailed.set(false);
                this.authStore.update({
                    userId: loggedInUserId,
                    organisationId: loggedInOrganisationId,
                    isLoggedIn: true,
                });
            }
        } catch (error: unknown) {
            this.authStore.update({
                userId: undefined,
                organisationId: undefined,
                isLoggedIn: false
            });

            if (error instanceof OrganisationError) {
                await this.tokenService.createToken(loginType, username, password, {
                    authenticationTarget: 'Server'
                });
                this.updateAuthData(username, password, rememberUser);
                this.appService.setLastLoginType(loginType);
            }
            throw error;
        }
    }

    /**
     * Login the user by authenticating the user with the provided credentials.
     *
     * @param {LoginType} loginType - The type of login being used, indicating the authentication method.
     * @param {string} username - The username of the user attempting to authenticate.
     * @param {string} password - The password of the user attempting to authenticate.
     * @param {boolean} rememberUser - The flag if the user wants to remember the credentials to re-login automatically
     * @throws {TokenAuthenticationError} - Error that occurs, when the user could not be authenticated
     * @throws {OrganisationError} - Error that occurs, while logging in
     * @throws {Error} - Error that occurs, while logging in
     */
    public async loginWithNavigation(username: string, password: string, rememberUser: boolean = false, loginType: LoginType = LOGIN_TYPE.USERNAME): Promise<void> {
        await this.login(username, password, rememberUser, loginType);
        await this.navigationService.navigateAfterLogin();
    }

    public async logout(azureLogout: boolean = false): Promise<void> {
        if (azureLogout && this.appQuery.getLastLoginType() === LOGIN_TYPE.AZURE) {
            await this.azureService.logout();
        }

        this.tokenService.clearTokenRefreshTimer();
        this.panelService.closeAll();
        this.clearStores();
        this.languageService.setBrowserLocaleAsCurrentIntlLocale();
        this.appService.showDebugMenu();

        this.notificationService.setHasReadNotificationInSession(false);
    }

    public clearStores(clearAuthStore: boolean = true): void {
        if (clearAuthStore) {
            this.authStore.reset();
        }
        this.akitaMimicService.resetStores([
            'app',
            'tutorial',
            'browser',
        ].concat(clearAuthStore ? [] : ['auth']));
        const amagnoStorage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);
        amagnoStorage.delete(AmagnoStorageKeyEnum.PANELS);
        this.recentTagValuesService.clear();
        this.recentSearchService.resetRecentSearches();
        this.listService.cleanActiveListsFromStore();
        this.appService.resetOnLogout();
    }

    public async setup(): Promise<void> {
        this.tokenService.scheduleTokenRefresh(this.tryToRefreshToken);
        if (this.tokenService.isCurrentTokenValid() || !this.authQuery.isRememberUser()) {
            return;
        }

        this.appService.setShowSplashScreen(true);

        if (!this.router.url.includes('logout')) {
            const reAuthenticationResult = await this.tryToReAuthenticate();

            if (reAuthenticationResult !== ReAuthenticationResultEnum.Success) {
                await this.logout();
                await this.navigationService.navigateToLogin();
                this.showReAuthenticationError(reAuthenticationResult);
            }
        }

        this.appService.setShowSplashScreen(false);
    }

    public continueAfterLoggedIn(func: () => Promise<boolean>): void {
        if (this.tokenService.isCurrentTokenValid()) {
            func()
                .then();
            return;
        }

        const funcStr = func.toString();
        if (!(funcStr in this.subscriptions)) {
            this.subscriptions[funcStr] = undefined;
        }

        // get rid of current subscription
        if (this.subscriptions[funcStr] !== undefined) {
            this.subscriptions[funcStr]?.unsubscribe();
        }

        this.subscriptions[funcStr] = this.authQuery.token$.pipe(
                skipWhile(() => !this.tokenService.isCurrentTokenValid()),
                switchMap(() => this.afterLoginNavigationObservable),
                filter(hasNavigated => hasNavigated),
            )
            .subscribe(async () => {
                if (await func()) {
                    if (this.subscriptions[funcStr] !== undefined) {
                        this.subscriptions[funcStr]?.unsubscribe();
                        delete this.subscriptions[funcStr];
                    }
                }
            });
    }

    public async resetPassword(email: string): Promise<boolean> {
        let isReset = false;
        try {
            await firstValueFrom(this.apiMeService.MeResetPassword({ userName: email }));
            isReset = true;
        } catch (err) {
            console.error(err);
        }
        return isReset;
    }

    public async isAuthenticated(showSplashscreen: boolean = false): Promise<boolean> {
        if (this.authQuery.getIsLoggedIn() && this.tokenService.isCurrentTokenValid()) {
            return true;
        }

        if (this.router.url.includes('logout')) {
            return false;
        }

        if (this.authQuery.isRememberUser()) {
            if (showSplashscreen) {
                this.appService.setShowSplashScreen(true);
            }

            const reAuthenticationResult = await this.tryToReAuthenticate();
            this.appService.setShowSplashScreen(false);

            if (reAuthenticationResult !== ReAuthenticationResultEnum.Success) {
                await this.logout();
                this.showReAuthenticationError(reAuthenticationResult);

                return false;
            }

            return true;
        }

        return false;
    }

    public async getToken(refreshTokenOnError: boolean = true, logoutOnFail: boolean = true): Promise<string> {
        let token = '';
        try {
            token = this.tokenService.getToken();
        } catch (error) {
            if (refreshTokenOnError && this.authQuery.isRememberUser()) {
                this.pendingReAuthentication ??= this.reAuthenticate();
                await this.pendingReAuthentication;
                return this.getToken(false, logoutOnFail);
            }
            if (logoutOnFail) {
                if (!this.hasTokenFailed()) {
                    this.hasTokenFailed.set(true);
                    this.sentryService.captureException(error);
                    this.showAuthenticationError('ERROR_AUTHENTICATION_EXPIRED');
                    await this.logout();
                    await this.navigationService.navigateToLogin();
                }
                return '';
            }
            throw (error);
        }
        return token;
    }

    public async getBearer(): Promise<string> {
        const token = await this.getToken();
        if (token) {
            return 'Bearer ' + token;
        }
        return '';
    }

    public updateUserAuthData(user: User): void {
        this.authStore.update({
            fullName: user?.fullName || user?.email,
        });
    }

    public async showSystemMessageIfSet(): Promise<void> {
        const selectedServer = this.serverSelectionService.getSelectedServer();
        if (!selectedServer) {
            return;
        }

        const serverAddress = await this.serverSelectionService.fetchServerInformationAndGetUpdatedServerAddress(selectedServer);
        const serverInformation = serverAddress?.serverInformation;
        this.dialogService.showSystemMessageDialog(serverInformation?.systemMessage);
    }

    private async reAuthenticate(): Promise<void> {
        this.tokenService.invalidateToken();
        try {
            const reAuthenticationResult = await this.tryToReAuthenticate();
            if (reAuthenticationResult !== ReAuthenticationResultEnum.Success) {
                this.showReAuthenticationError(reAuthenticationResult);
            }
        } catch (error) {
            this.sentryService.captureException(error, 'Re-authentication while renewing the token failed.');
        } finally {
            this.pendingReAuthentication = undefined;
        }
    }

    private showReAuthenticationError(reAuthenticationResult: ReAuthenticationResultEnum): void {
        this.showAuthenticationError(reAuthenticationResult === ReAuthenticationResultEnum.AzureLoginFailed
                                     ? 'ERROR_AZURE_LOGIN_FAILED'
                                     : 'ERROR_AUTHENTICATION_FAILED');
    }

    private showAuthenticationError(translationKey: TranslationKey): void {
        this.closeAuthenticationErrorSnackbar();
        this.authenticationErrorSnackbar = this.snackbarService.showPermanentSnackbar(
            AppSnackbarTypeEnum.Error,
            'permanent-snackbar-' + translationKey,
            translationKey,
            undefined,
            'BUTTON.OK'
        );
    }

    private closeAuthenticationErrorSnackbar(): void {
        if (!this.authenticationErrorSnackbar) {
            return;
        }

        this.modalService.closeModal(this.authenticationErrorSnackbar);
        this.authenticationErrorSnackbar = undefined;
    }

    private updateAuthData(username?: string, password?: string, rememberUser: boolean = false): void {
        this.authStore.update({
            username: username ? this.obscureCredentialForStore(username) : undefined,
            password: password ? this.obscureCredentialForStore(password) : undefined,
            rememberUser
        });
    }

    private obscureCredentialForStore(credential: string): string {
        let code = `${this.getBase64EncodedValue(Date.now())}.${this.getBase64EncodedValue(credential)}.${this.getBase64EncodedValue(Date.now())}`;
        const canvas = this.document.createElement('canvas');
        canvas.width = code.length;
        canvas.height = 1;
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;
        const imageData: ImageData = context.createImageData(canvas.width, 1);
        let char = 0;
        for (let i = 0; i < imageData.data.length; i += 4) {
            const codePoint = code.codePointAt(char);
            if (codePoint) {
                imageData.data[i] = codePoint;
                imageData.data[i + 3] = 255;
            }

            ++char;
        }
        context.putImageData(imageData, 0, 0);
        const dataUrl = canvas.toDataURL('png');
        code = dataUrl.split('base64,')[1];
        return code;
    }

    private unveilCredentialFromStore(credential: string): Promise<string> {
        return new Promise<string>(resolve => {
            const dataUrl = 'data:image/png;base64,' + credential;
            const image = new Image();
            image.addEventListener('error', () => {
                resolve('');
            });
            image.addEventListener('load', () => {
                const canvas = this.document.createElement('canvas');
                canvas.width = image.width;
                canvas.height = image.height;
                const context = canvas.getContext('2d') as CanvasRenderingContext2D;
                context.drawImage(image, 0, 0);
                const imageData: ImageData = context.getImageData(0, 0, canvas.width, canvas.height);
                let text = '';
                for (let i = 0; i < imageData.data.length; i += 4) {
                    text += String.fromCodePoint(imageData.data[i]);
                }
                const base64TextArray = text.split('.');
                text = this.getBase64DecodedValue(base64TextArray[1]);
                resolve(text);
            });
            image.src = dataUrl;
        });
    }

    private getBase64EncodedValue(value: string | number): string {
        const bytes = new TextEncoder().encode(String(value));
        return this.window.btoa(Array.from(bytes, byte => String.fromCodePoint(byte))
            .join(''));
    }

    private getBase64DecodedValue(value: string): string {
        try {
            const bytes = Uint8Array.from(this.window.atob(value), c => c.codePointAt(0) ?? 0);
            
            return new TextDecoder().decode(bytes);
        } catch (error) {
            this.sentryService.captureException(error);

            return '';
        }
    }
}
